import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, CloseIcon, CriticalIcon, InfoIcon, WarningIcon } from './icons';

/** Semantic tone of a toast — drives the accent colour + icon. */
export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

/** A single toast to display. */
export interface ToastOptions {
  /** Short headline, e.g. "Saved". */
  title: string;
  /** Optional supporting line, e.g. "Environment updated." */
  description?: string | undefined;
  /** Tone; defaults to `success`. */
  variant?: ToastVariant | undefined;
  /** Auto-dismiss delay in ms; defaults to 4000 (6000 for errors). 0 = sticky. */
  duration?: number | undefined;
}

interface ActiveToast {
  id: number;
  title: string;
  description: string;
  variant: ToastVariant;
  duration: number;
}

/** The toast API exposed via {@link useToast}. */
export interface ToastApi {
  toast: (options: ToastOptions) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

/**
 * A no-op API used as the context default. It keeps {@link useToast} safe to call
 * outside a provider (e.g. a component rendered in isolation in a test) — the app
 * root always mounts a real {@link ToastProvider}, so production always shows them.
 */
const NOOP_TOAST_API: ToastApi = {
  toast: () => undefined,
  success: () => undefined,
  error: () => undefined,
  info: () => undefined,
};

const ToastContext = createContext<ToastApi>(NOOP_TOAST_API);

/** Monotonic id source (module-scoped; never reset). */
let nextToastId = 0;

const VARIANT_ICON = {
  success: CheckIcon,
  error: CriticalIcon,
  info: InfoIcon,
  warning: WarningIcon,
} as const;

/** Accent + icon colours per variant, defined at token level for both themes. */
const VARIANT_ACCENT: Record<ToastVariant, string> = {
  success: 'border-l-emerald-500 text-emerald-600 dark:text-emerald-400',
  error: 'border-l-red-500 text-red-600 dark:text-red-400',
  info: 'border-l-brand-500 text-brand-600 dark:text-brand-300',
  warning: 'border-l-amber-500 text-amber-600 dark:text-amber-400',
};

/**
 * App-wide toast notifications (bottom-right). Provides {@link useToast} for
 * transient success/error/info feedback after a save or other action, so the user
 * gets clear confirmation without a blocking dialog.
 *
 * Accessibility: the stack is an `aria-live` region — polite for success/info,
 * assertive for errors — so screen readers announce each toast. Every toast can be
 * dismissed manually and auto-dismisses on a timer (respecting the timer, never the
 * animation, for reduced-motion users).
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number): void => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (options: ToastOptions): void => {
      const variant = options.variant ?? 'success';
      const id = nextToastId++;
      const duration = options.duration ?? (variant === 'error' ? 6000 : 4000);
      setToasts((current) => [
        ...current,
        { id, title: options.title, description: options.description ?? '', variant, duration },
      ]);
      if (duration > 0) {
        const handle = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, handle);
      }
    },
    [dismiss],
  );

  // Clear any pending timers on unmount.
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((handle) => clearTimeout(handle));
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      success: (title, description) => toast({ title, description, variant: 'success' }),
      error: (title, description) => toast({ title, description, variant: 'error' }),
      info: (title, description) => toast({ title, description, variant: 'info' }),
    }),
    [toast],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed bottom-0 right-0 z-[60] flex w-full max-w-sm flex-col gap-2 p-4">
          <div aria-live="polite" className="flex flex-col gap-2">
            {toasts.filter((t) => t.variant !== 'error').map((t) => (
              <ToastCard key={t.id} toast={t} onDismiss={dismiss} />
            ))}
          </div>
          <div aria-live="assertive" className="flex flex-col gap-2">
            {toasts.filter((t) => t.variant === 'error').map((t) => (
              <ToastCard key={t.id} toast={t} onDismiss={dismiss} />
            ))}
          </div>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/** A single rendered toast card. */
function ToastCard({ toast, onDismiss }: { toast: ActiveToast; onDismiss: (id: number) => void }): JSX.Element {
  const Icon = VARIANT_ICON[toast.variant];
  return (
    <div
      role="status"
      className={
        'pointer-events-auto flex items-start gap-3 rounded-lg border border-slate-200 border-l-4 bg-white px-4 py-3 shadow-lg ' +
        'motion-safe:animate-[toast-in_180ms_ease-out] dark:border-navy-border dark:bg-navy-surface ' +
        VARIANT_ACCENT[toast.variant]
      }
    >
      <Icon size={18} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{toast.title}</p>
        {toast.description !== '' ? (
          <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{toast.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
        className="-m-1 shrink-0 rounded p-1 text-slate-400 transition-colors hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:hover:text-slate-200"
      >
        <CloseIcon size={16} />
      </button>
    </div>
  );
}

/**
 * Access the toast API. Outside a {@link ToastProvider} it returns a safe no-op
 * (see {@link NOOP_TOAST_API}); the app root always mounts a real provider.
 */
export function useToast(): ToastApi {
  return useContext(ToastContext);
}
